import { useState } from "react";

import { Box, FormHelperText, Typography } from "@mui/material";

import { LessonStep } from "@/types";
import { htmlContentStyles, isHtmlContentEmpty } from "@/utils/htmlContentStyle";
import { renderMathInHtml } from "@/utils/mathRenderer";

import { EditingField } from "../Problems/features";

import LessonPlanActionButtons from "./LessonPlanActionButtons";
import LessonPlanStep from "./LessonPlanStep";

interface LessonPlanItemProps {
  lessonPlan: {
    title: string;
    overview: string;
    duration: string;
    steps: LessonStep[];
  };
  onUpdate: (title: string, overview: string, steps: LessonStep[]) => void;
}
interface ValidationErrors {
  title?: string;
  overview?: string;
}

const LessonPlanItem = ({ lessonPlan, onUpdate }: LessonPlanItemProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedLessonPlan, setEditedLessonPlan] = useState<typeof lessonPlan>(lessonPlan);
  const [errors, setErrors] = useState<ValidationErrors>({});

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    if (!editedLessonPlan.title?.trim()) {
      newErrors.title = "Title is required";
      isValid = false;
    }

    if (isHtmlContentEmpty(editedLessonPlan.overview)) {
      newErrors.overview = "Overview is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (field: keyof typeof lessonPlan, value: unknown) => {
    setEditedLessonPlan((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for the field being changed
    if (errors[field as keyof ValidationErrors]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleCancel = () => {
    setEditedLessonPlan(lessonPlan);
    setErrors({});
    setIsEditing(false);
  };

  const handleSave = () => {
    if (validateForm()) {
      onUpdate(editedLessonPlan.title, editedLessonPlan.overview, lessonPlan.steps);
      setIsEditing(false);
    }
  };

  const handleStepUpdate = (index: number, updatedTitle: string, updatedContent: string) => {
    const updatedSteps = lessonPlan.steps.map((step, i) =>
      i === index ? {
        ...step,
        title: updatedTitle,
        content: updatedContent
      } : step
    );
    onUpdate(lessonPlan.title, lessonPlan.overview, updatedSteps);
  };

  return (
    <Box
      mb={4}
      px={0.25}
      sx={{
        color: "text.primary",
      }}
    >
      {isEditing ? (
        <>
          <Box sx={{ mb: 2 }}>
            <EditingField
              label="Title"
              value={editedLessonPlan.title}
              onChange={(value) => handleChange("title", value)}
              placeholder="Enter step title"
              isEditing={isEditing}
              error={!!errors.title}
            />
            {errors.title && <FormHelperText error>{errors.title}</FormHelperText>}
          </Box>
          <Box sx={{ mb: 2 }}>
            <EditingField
              initialHeight={600}
              label="Overview"
              value={editedLessonPlan.overview}
              onChange={(value) => handleChange("overview", value)}
              type="textarea"
              placeholder="Enter the overview"
              isEditing={isEditing}
              error={!!errors.overview}
            />
            {errors.overview && (
              <FormHelperText error>{errors.overview}</FormHelperText>
            )}
          </Box>
        </>
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 1.5,
            }}
          >
            <Typography
              variant="text13B"
              sx={{
                flex: "1 1 auto",
                minWidth: 0,
              }}
            >
              {lessonPlan.title}
            </Typography>
            <Box
              sx={{
                flex: "0 1 140px",
                minWidth: 24,
                borderTop: "1px dashed",
                borderColor: "divider",
                opacity: 0.9,
              }}
            />
            <LessonPlanActionButtons
              isEditing={isEditing}
              onEdit={() => setIsEditing(true)}
              onSave={handleSave}
              onCancel={handleCancel}
            />
          </Box>

          <Typography
            dangerouslySetInnerHTML={{ __html: renderMathInHtml(lessonPlan.overview) }}
            variant="text13L"
            sx={{
              ...htmlContentStyles,
            }}
          />
        </>
      )}
      {isEditing && (
        <Box mb={2}>
          <LessonPlanActionButtons
            isEditing={isEditing}
            onEdit={() => setIsEditing(true)}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        </Box>
      )}
      {lessonPlan.steps.map((step: LessonStep, index: number) => (
        <LessonPlanStep
          key={index}
          index={index}
          step={step}
          onUpdate={handleStepUpdate}
        />
      ))}
    </Box>
  );
};

export default LessonPlanItem;
